/**
 * Application Profiler API client — talks to /api/profiling-agent/* endpoints
 * on the ZTD backend.
 *
 * Previous versions of this file targeted a /api/v1/profiler/* namespace
 * that was never built. This rewrite aligns every call with the endpoints
 * that actually exist in
 * ztd_app/api/modules/data_profiler/agent_endpoints_router.py,
 * adapting response shapes inline so UI components receive the
 * TypeScript shapes they expect.
 */

import { clearAuth } from "../auth";

const BASE = "/api/profiling-agent";

// ── Types ────────────────────────────────────────────────────────

export interface ProfilerConfig {
  max_sample_rows: number;
  pii_detection_model: string;
  pii_confidence_threshold: number;
  column_timeout_seconds: number;
}

export interface ProfilerConfigUpdate {
  max_sample_rows?: number;
  pii_detection_model?: string;
  pii_confidence_threshold?: number;
  column_timeout_seconds?: number;
}

export interface StartProfileRequest {
  source_id: string;
  /** Project context — wired through to ProfilingRun.project_id so this run
   *  surfaces in the project's history view (FR105-FR109). */
  project_id?: string;
  sample_rows?: number;
  detect_pii?: boolean;
  assess_quality?: boolean;
  detect_relationships?: boolean;
  detect_keys?: boolean;
  /** Opt-in value-based relationship suggestions for file/folder extracts (ignored by the backend for databases). */
  deep_relationships?: boolean;
  config_overrides?: Record<string, unknown>;
}

export type RunPhase =
  | "uploading"
  | "connecting"
  | "profiling"
  | "analyzing"
  | "interpreting"
  | "complete"
  | "completed"
  | "failed"
  | "queued"
  | "unknown";

export interface RunStatus {
  session_id: string;
  status: RunPhase;
  tables_profiled: number;
  tables_total: number;
  current_table?: string;
  aggregate_score?: number;
  elapsed_seconds?: number;
  error?: string;
}

export type SecondaryScanStatus =
  | "not_applicable"
  | "pending"
  | "clean"
  | "infected"
  | "timed_out"
  | "error";

export interface SecondaryScanStatusResult {
  session_id: string;
  secondary_scan_status: SecondaryScanStatus;
}

export interface ColumnProfile {
  name: string;
  dtype: string;
  null_pct: number;
  unique_pct: number;
  is_pk: boolean;
  is_fk: boolean;
  is_pii: boolean;
  pii_type?: string;
  sample_values?: string[];
  min?: string | number;
  max?: string | number;
  mean?: number;
  stddev?: number;
  pattern?: string;
}

export interface DQIssue {
  id: string;
  severity: "critical" | "poor" | "moderate" | "warning" | "good" | "excellent";
  category: string;
  description: string;
  /** Table this issue belongs to. Backend delivers it as `table_name`; getDQ
   * normalises it to `table` too (the Findings tab keys its count/filter on it). */
  table?: string;
  table_name?: string;
  affected_columns?: string[];
  remediation?: string;
  rule_id?: string;
  /** Up to 25 sample violating rows captured at profile time. */
  sample_failures?: Array<Record<string, unknown>>;
  /** Column order for rendering sample_failures as a table. */
  sample_failure_columns?: string[];
}

/** Present on a table whose sample was cut short by the per-table size budget
 *  (ZTD_PROFILER_SAMPLE_MAX_BYTES), so its profile rests on fewer rows than were
 *  requested. Absent for a normally-sampled or full-scan table. */
export interface SampleCap {
  rows_kept: number;
  rows_requested: number;
  est_bytes: number;
  budget_bytes: number;
}

export interface TableProfile {
  table_name: string;
  /** Authoritative row count (from DB stats / pg_class / INFORMATION_SCHEMA). */
  row_count: number;
  /** Underlying table size (DB stats). Same as row_count when known. */
  row_count_actual?: number;
  /** Rows that the profiler actually sampled (DataFrame length). When the
   *  source applied a sample cap, this is < row_count; equal otherwise. */
  row_count_sample?: number;
  /** Set only when the size budget capped this table's sample. */
  sample_capped?: SampleCap;
  column_count: number;
  score: number;
  severity: "critical" | "poor" | "moderate" | "warning" | "good" | "excellent";
  columns: ColumnProfile[];
  issues: DQIssue[];
  quality_dimensions?: Record<string, number>;
}

/** The phases a profiling run had switched on (the backend's `_resolve_run_options`, as stored on the run). */
export interface RunOptions {
  dq: boolean;
  keys: boolean;
  relationships: boolean;
}

export interface ProfileResults {
  session_id: string;
  source_name: string;
  source_type: string;
  tables: TableProfile[];
  aggregate_score: number;
  total_tables: number;
  total_rows: number;
  /** Sum of underlying table sizes (DB stats). Same as total_rows when no sampling cap was hit. */
  total_rows_actual?: number;
  /** Sum of sampled rows actually profiled. */
  total_rows_sample?: number;
  total_columns: number;
  profiled_at: string;
  /** Which phases this run had on; absent (older backend) means a full run. */
  run_options?: RunOptions;
}

export interface Relationship {
  source_table: string;
  source_column: string;
  target_table: string;
  target_column: string;
  confidence: number;
  cardinality: string;
  type: "pk_fk" | "inferred" | "declared";
}

export interface RelationshipResults {
  relationships: Relationship[];
  graph?: Record<string, unknown>;
  pk_candidates: Record<
    string,
    Array<{ columns: string[]; confidence: number }>
  >;
  fk_candidates: Record<
    string,
    Array<{ columns: string[]; target_table: string; confidence: number }>
  >;
}

export interface DQAssessment {
  session_id: string;
  dimension_scores: Record<string, number>;
  issues: DQIssue[];
  rules: Array<{
    id: string;
    rule_text: string;
    rule_type: string;
    table: string;
    column?: string;
    state: string;
  }>;
  remediation_plan?: Array<{
    priority: number;
    issue_id: string;
    action: string;
    impact: string;
  }>;
  /**
   * Great Expectations suite per table. Backend emits a map keyed by
   * table name, each value being a partial GE suite JSON (expectations[]).
   */
  ge_suite_by_table?: Record<string, { expectations?: any[] }>;
}

export interface UploadResult {
  session_id: string;
  file_name: string;
  source_name?: string;
  file_size: number;
  tables_detected: number;
  source_type: string;
}

/**
 * One discovered table entry as returned by /db-connect.
 * `row_count_estimate` and `column_count` are populated when the
 * backend ran `discover_tables(include_stats=True)`; both are
 * nullable to keep the picker resilient when stats failed (e.g.
 * views, permissions, unsupported engines).
 */
export interface DiscoveredTable {
  name: string;
  schema?: string;
  type?: "table" | "view" | string;
  row_count_estimate?: number | null;
  column_count?: number | null;
}

/**
 * Tables the profiler table-picker leaves UNCHECKED by default:
 *  - VIEWS — a view is a query, not stored rows; it has no native row count
 *    (excluded from the size estimate) and is rarely a profiling target.
 *  - Salesforce-sync trigger / journal tables (SF_SYNC*, *SYNC_TRGR*) —
 *    integration plumbing, not source data.
 * Users can still tick any of these to include them in the run.
 */
export function isDefaultExcludedTable(t: DiscoveredTable): boolean {
  if (t.type === "view") return true;
  const n = (t.name || "").toUpperCase();
  return n.includes("SF_SYNC") || n.includes("SYNC_TRGR");
}

export interface DBConnectResult {
  session_id: string;
  tables_detected: number;
  schema_name: string;
  source_type: string;
  tables: DiscoveredTable[];
  /** The saved project connection this session profiles (null when none could be saved). */
  connection_id?: string | null;
}

/** Response from GET /schemas/{session_id} (#682). */
export interface SchemaListResult {
  schemas: string[];
  current_schema: string | null;
}

// ── Multi-file / Folder types (unchanged — backend passes these through) ──

export interface DiscoveredFile {
  file_id: string;
  path: string;
  relative_path: string;
  extension: string;
  size_bytes: number;
  logical_group: string;
  status:
    | "processable"
    | "unreadable"
    | "empty"
    | "encoding_ambiguous"
    | "unsupported";
  encoding: string | null;
  encoding_confidence: number;
  error_reason: string | null;
  column_count: number | null;
  estimated_row_count: number | null;
  columns: string[];
  sheet_names: string[];
  included: boolean;
  exclusion_reason: string | null;
  user_override: boolean;
}

export interface SchemaReconciliation {
  group_name: string;
  file_count: number;
  common_columns: string[];
  all_columns: string[];
  consistency: "identical" | "partial_overlap" | "disjoint";
  variations: Array<Record<string, unknown>>;
  mismatches: Array<Record<string, unknown>>;
  file_column_map: Record<string, string[]>;
}

export interface PreFlightReport {
  root_path: string;
  total_files: number;
  processable_count: number;
  unreadable_count: number;
  empty_count: number;
  encoding_ambiguous_count: number;
  unsupported_count: number;
  total_size_bytes: number;
  total_size_mb: number;
  files_by_type: Record<string, number>;
  files: DiscoveredFile[];
  schema_reconciliation: SchemaReconciliation[];
  warnings: string[];
  errors: string[];
}

/**
 * Immediate response from upload-folder / upload-files. The extract +
 * pre-flight scan runs in the background (can take several minutes for a
 * multi-GB archive) — this comes back as soon as the upload itself lands,
 * with no `pre_flight` yet. Poll `getFolderPreflightStatus` for the report.
 */
export interface FolderUploadResult {
  session_id: string;
  source_name: string;
  source_type: "folder";
  status: "processing";
}

export interface FolderPreflightStatus {
  session_id: string;
  source_name: string;
  source_type: string;
  status: "processing" | "ready" | "failed";
  pre_flight?: PreFlightReport;
  error_message?: string;
}

export interface FolderFilterRequest {
  session_id: string;
  include_file_ids?: string[];
  exclude_file_ids?: string[];
}

export interface FolderFilterResult {
  processable_count: number;
  selected_files: DiscoveredFile[];
}

// ── SSE Event Types (backend doesn't emit these yet — types stay for UI) ─

export interface SSETableStart {
  tableName: string;
  tierAssigned: string;
}

export interface SSETableComplete {
  tableName: string;
  score: number;
  severity: string;
  columnCount: number;
  rowCount: number;
  issues: DQIssue[];
}

export interface SSETableError {
  tableName: string;
  error: string;
  retryable: boolean;
}

export interface SSESessionComplete {
  aggregateScore: number;
  tablesProfiled: number;
  elapsed: number;
}

// ── HTTP helper ─────────────────────────────────────────────────

/** Strip DOM / React-fiber / function references from an arbitrary
 *  value so it survives `JSON.stringify`.
 *
 *  Triggered by a Vite+React-19 quirk where Synthetic-event handlers
 *  occasionally leak the originating `HTMLButtonElement` (with its
 *  `__reactFiber$…` back-reference) into state that we later try to
 *  POST. The form-submit dispatcher pulls `e.currentTarget` into a
 *  ref chain that survives reset; even though the surface `dbForm`
 *  state looks clean, the cloned object passed to fetch can pick up
 *  one of those refs. `JSON.stringify` then trips
 *  "Converting circular structure to JSON --> starting at object with
 *  constructor 'HTMLButtonElement'". The robust fix is to whitelist
 *  serialisable values at the boundary.
 */
function _sanitizeForJSON(
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  if (value === null || value === undefined) return value;
  const t = typeof value;
  if (t === "string" || t === "number" || t === "boolean") return value;
  if (t === "function" || t === "symbol") return undefined;
  if (value instanceof Date) return value.toISOString();
  // Reject DOM nodes outright.
  if (typeof Element !== "undefined" && value instanceof Element)
    return undefined;
  if (typeof Node !== "undefined" && value instanceof Node) return undefined;
  if (typeof Event !== "undefined" && value instanceof Event) return undefined;
  // React's SyntheticEvent is not `instanceof Event` (it's a plain object
  // React constructs, not a subclass of the DOM's Event), so the checks
  // above miss it. Duck-type on the Event-shaped methods every synthetic
  // and native event exposes, so a handler that leaks its event argument
  // into a request body (e.g. by being passed directly as a DOM onClick)
  // gets stripped here instead of silently serializing into a garbage
  // field value.
  if (
    typeof (value as { preventDefault?: unknown }).preventDefault ===
      "function" &&
    typeof (value as { stopPropagation?: unknown }).stopPropagation ===
      "function"
  )
    return undefined;
  if (typeof value === "object") {
    if (seen.has(value as object)) return undefined;
    seen.add(value as object);
    if (Array.isArray(value)) {
      return value.map((v) => _sanitizeForJSON(v, seen));
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      // Strip React-internal back-references — they are the usual
      // vector for circular structures coming through.
      if (k.startsWith("__react") || k.startsWith("$$typeof")) continue;
      const cleaned = _sanitizeForJSON(v, seen);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return out;
  }
  return undefined;
}

/** JSON.stringify variant that pre-sanitises the payload — see
 *  `_sanitizeForJSON` rationale. Use this everywhere the request
 *  body comes from React state. */
export function safeStringify(value: unknown): string {
  return JSON.stringify(_sanitizeForJSON(value));
}

// Methods the backend's CSRF middleware requires a double-submit token for
// (mirrors SAFE_METHODS in ztd_app/api/core/middleware/csrf.py).
const CSRF_REQUIRED_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Read CSRF token from cookie for inclusion in mutating requests. */
function getCsrfToken(): string {
  return document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "";
}

/** Thrown by `request()` on a non-2xx response. `code` is populated when the
 *  backend sent a structured `detail: {code, message}` (used by start_run's
 *  secondary-scan gate so callers can branch on `code` — e.g. to poll and
 *  auto-retry — instead of string-matching the human-readable message). */
export class ProfilerApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = "ProfilerApiError";
    this.code = code;
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(CSRF_REQUIRED_METHODS.has(method)
      ? { "X-CSRF-Token": getCsrfToken() }
      : {}),
    ...(init?.headers as Record<string, string>),
  };
  const res = await fetch(`${BASE}${url}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (res.status === 401) {
    clearAuth();
    throw new Error("Session expired — please log in again.");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = body.detail;
    const structured =
      detail && typeof detail === "object"
        ? (detail as Record<string, unknown>)
        : null;
    const message = structured ? structured.message : detail;
    const code = structured ? structured.code : undefined;
    throw new ProfilerApiError(
      (typeof message === "string" && message) ||
        body.title ||
        `Request failed: ${res.status}`,
      typeof code === "string" ? code : undefined,
    );
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

// ── Response adapters — transform backend shapes → frontend types ─

function toUploadResult(raw: Record<string, any>, file: File): UploadResult {
  const sess = raw.session_id || raw.id || "";
  const isMultiSheet = !!raw.is_multi_sheet;
  const tablesDetected = isMultiSheet
    ? Array.isArray(raw.sheets)
      ? raw.sheets.length
      : 1
    : 1;
  return {
    session_id: sess,
    file_name: file.name,
    source_name: raw.source_name || file.name,
    file_size: file.size,
    tables_detected: tablesDetected,
    source_type: raw.source_type || "file",
  };
}

function toDBConnectResult(raw: Record<string, any>): DBConnectResult {
  const rawTables = Array.isArray(raw.tables) ? raw.tables : [];
  // Normalize: backend may return either `string[]` (legacy) or
  // `{name, schema, type, row_count_estimate?, column_count?}[]`.
  const tables: DiscoveredTable[] = rawTables.map((t: any): DiscoveredTable => {
    if (typeof t === "string") return { name: t };
    return {
      name: t?.name || "",
      schema: t?.schema,
      type: t?.type,
      row_count_estimate:
        typeof t?.row_count_estimate === "number" ? t.row_count_estimate : null,
      column_count: typeof t?.column_count === "number" ? t.column_count : null,
    };
  });
  return {
    session_id: raw.session_id || "",
    tables_detected: tables.length,
    schema_name: raw.schema || raw.schema_name || "default",
    source_type: raw.source_type || "database",
    tables,
    connection_id:
      typeof raw.connection_id === "string" ? raw.connection_id : null,
  };
}

function toRunStatus(raw: Record<string, any>): RunStatus {
  // Legacy backend returns various status strings — map them to RunPhase.
  const rawStatus = (raw.status || raw.step || "unknown").toLowerCase();
  const statusMap: Record<string, RunPhase> = {
    queued: "queued",
    profiling: "profiling",
    running: "profiling",
    profiled: "complete",
    completed: "completed",
    complete: "complete",
    failed: "failed",
    uploading: "uploading",
    connecting: "connecting",
    analyzing: "analyzing",
    interpreting: "interpreting",
  };
  const status: RunPhase = statusMap[rawStatus] || "unknown";

  const totalTables = raw.table_count ?? raw.tables_total ?? 0;
  const profiledTables =
    raw.tables_profiled ??
    (status === "completed" || status === "complete" ? totalTables : 0);

  return {
    session_id: raw.session_id || "",
    status,
    tables_profiled: profiledTables,
    tables_total: totalTables,
    current_table: raw.current_table,
    aggregate_score: raw.aggregate_score,
    elapsed_seconds: raw.elapsed_seconds,
    error: raw.error_message || raw.error,
  };
}

/**
 * Redistribute session-level `dq_issues` back onto each table, keyed by
 * `issue.table`. The backend currently emits statistical DQ findings as
 * a flat list; the UI's TableCard / DQ-tab reads them per-table.
 */
function buildIssuesByTable(rawDqIssues: any[]): Record<string, any[]> {
  const out: Record<string, any[]> = {};
  for (const iss of Array.isArray(rawDqIssues) ? rawDqIssues : []) {
    const tbl = iss?.table;
    if (!tbl) continue;
    // Carry sample_failures + sample_failure_columns through so the
    // DQ tab drill-down has the data it needs. Previously they were
    // dropped here, leaving the UI with nothing to expand.
    (out[tbl] ||= []).push({
      id: iss.id || "",
      severity: iss.severity || "moderate",
      category: iss.category || "",
      description: iss.description || "",
      affected_columns:
        iss.affected_columns && iss.affected_columns.length > 0
          ? iss.affected_columns
          : iss.column
            ? [iss.column]
            : [],
      remediation: iss.suggested_fix || iss.fix,
      sample_failures: Array.isArray(iss.sample_failures)
        ? iss.sample_failures
        : undefined,
      sample_failure_columns: Array.isArray(iss.sample_failure_columns)
        ? iss.sample_failure_columns
        : undefined,
    });
  }
  return out;
}

/** Exported for tests: it is the one place a backend profile field can be silently dropped on its way to the UI. */
export function toProfileResults(raw: Record<string, any>): ProfileResults {
  // v2 backend returns a flat `tables: [...]` array; legacy backends returned
  // `profiles: [{table, profile: {...}}]`. Prefer the new key; fall back to
  // the old one so legacy responses (and any test fixtures) still work.
  const profiles = Array.isArray(raw.tables)
    ? raw.tables
    : Array.isArray(raw.profiles)
      ? raw.profiles
      : [];
  const issuesByTable = buildIssuesByTable(raw.dq_issues || []);
  const tables: TableProfile[] = profiles.map((entry: any) => {
    // Unwrap the {table, profile} envelope if present.
    const tableName = entry.table || entry.name || entry.table_name || "";
    const p =
      entry.profile && typeof entry.profile === "object"
        ? entry.profile
        : entry;
    const md = p.metadata && typeof p.metadata === "object" ? p.metadata : {};
    const dbMd =
      p.db_metadata && typeof p.db_metadata === "object" ? p.db_metadata : {};
    // Column list lives in p.schema (the canonical field from the profiler).
    const rawColumns = Array.isArray(p.schema)
      ? p.schema
      : Array.isArray(p.columns)
        ? p.columns
        : [];
    // Row count: prefer the DB's own count (from pg_class/INFORMATION_SCHEMA)
    // over the sampled-DataFrame length. TABLESAMPLE returns ~1% which
    // massively undercounts the Summary card. `row_count_actual` is set by
    // discover_catalog(include_row_counts=True) in db_introspector.py.
    const actualRows =
      dbMd.row_count_actual ??
      p.row_count_actual ??
      p.row_count ??
      md.rows ??
      0;
    // Sample size = DataFrame length actually profiled. When the profiler
    // ran against a TABLESAMPLE / LIMIT clause this is < actualRows; when
    // the source had no sampling applied (small CSV, "All rows" mode), the
    // sample == actual.
    const sampleRows: number | undefined =
      typeof p.row_count_sample === "number"
        ? p.row_count_sample
        : typeof p.row_count === "number" && p.row_count !== actualRows
          ? p.row_count
          : typeof md.sample_rows === "number"
            ? md.sample_rows
            : undefined;
    // Map the profiler's per-table `quality` block to the UI's expected
    // `quality_dimensions` shape. Completeness / Uniqueness / Consistency
    // come from the backend; Validity is derived from severity if absent.
    const q = p.quality && typeof p.quality === "object" ? p.quality : {};
    const overall = q.overall?.score ?? q.overall ?? 0;
    const quality_dimensions = p.quality_dimensions ?? {
      completeness: q.completeness?.score ?? q.completeness ?? overall,
      uniqueness: q.uniqueness?.score ?? q.uniqueness ?? overall,
      consistency: q.consistency?.score ?? q.consistency ?? overall,
      validity: q.validity?.score ?? q.validity ?? overall,
    };
    // Severity from score when backend didn't provide one.
    const derivedSeverity =
      p.severity ||
      (overall >= 90
        ? "excellent"
        : overall >= 75
          ? "good"
          : overall >= 60
            ? "moderate"
            : overall >= 40
              ? "poor"
              : "critical");
    return {
      table_name: tableName,
      row_count: actualRows,
      row_count_actual: actualRows,
      row_count_sample: sampleRows,
      sample_capped:
        p.sample_capped && typeof p.sample_capped === "object"
          ? (p.sample_capped as SampleCap)
          : undefined,
      column_count: p.column_count ?? md.columns ?? rawColumns.length,
      score: p.quality_score ?? p.score ?? overall ?? 0,
      severity: derivedSeverity,
      columns: rawColumns.map(
        (c: any): ColumnProfile => ({
          name: c.name || c.column || "",
          dtype: c.dtype || c.data_type || "",
          null_pct: c.null_pct ?? c.null_percentage ?? 0,
          unique_pct: c.unique_pct ?? c.unique_percentage ?? 0,
          is_pk: !!c.is_pk,
          is_fk: !!c.is_fk,
          is_pii: !!c.is_pii,
          pii_type: c.pii_type,
          sample_values: c.sample_values,
          min: c.min,
          max: c.max,
          mean: c.mean,
          stddev: c.stddev,
          pattern: c.pattern,
        }),
      ),
      // Pull per-table issues from the session-level dq_issues if the
      // profile didn't carry its own list. This is what populates the
      // "N issues" badge on each TableCard and the DQ tab in the side panel.
      issues:
        Array.isArray(p.issues) && p.issues.length > 0
          ? p.issues
          : issuesByTable[tableName] || [],
      quality_dimensions,
    };
  });

  return {
    session_id: raw.session_id || "",
    source_name: raw.source_name || "",
    source_type: raw.source_type || "",
    tables,
    aggregate_score: raw.aggregate_score ?? 0,
    // v2 returns total_tables/total_columns directly; legacy returned
    // table_count/column_count. Sum the per-table row counts ourselves —
    // legacy backends derived raw.total_rows from a 1% TABLESAMPLE which
    // undercounts by ~100×, and v2's total_rows already matches our sum.
    total_tables: raw.total_tables ?? raw.table_count ?? tables.length,
    total_rows: tables.reduce((s, t) => s + (t.row_count || 0), 0),
    total_rows_actual:
      raw.total_rows_actual ??
      tables.reduce((s, t) => s + (t.row_count_actual ?? t.row_count ?? 0), 0),
    total_rows_sample: tables.reduce(
      (s, t) => s + (t.row_count_sample ?? t.row_count ?? 0),
      0,
    ),
    total_columns:
      raw.total_columns ??
      raw.column_count ??
      tables.reduce((s, t) => s + (t.column_count || 0), 0),
    // Prefer the session's completed_at timestamp; fall back to other keys.
    profiled_at:
      raw.profiled_at ||
      raw.completed_at ||
      raw.profiled_time ||
      raw.updated_at ||
      "",
  };
}

/**
 * POST a multipart/form-data body via XMLHttpRequest instead of fetch —
 * fetch has no upload-progress API at all (a browser platform limitation,
 * not something polyfillable), so a large file/zip upload showed nothing
 * but a spinner for however long the transfer actually took. XHR's
 * `upload.onprogress` is the only way to report real percentage.
 */
function uploadMultipartWithProgress(
  url: string,
  formData: FormData,
  onProgress?: (loaded: number, total: number) => void,
): Promise<any> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.setRequestHeader("X-CSRF-Token", getCsrfToken());
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status === 401) {
        clearAuth();
        reject(new Error("Session expired — please log in again."));
        return;
      }
      let body: Record<string, any> = {};
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch {
        body = {};
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body);
      } else {
        reject(new Error(body.detail || `Upload failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed: network error"));
    xhr.send(formData);
  });
}

// ── API Methods ──────────────────────────────────────────────────

export const profilerApi = {
  /** Upload a single file for profiling (FR5). */
  uploadFile: async (file: File, projectId?: string): Promise<UploadResult> => {
    const formData = new FormData();
    formData.append("file", file);
    if (projectId) formData.append("project_id", projectId);

    const res = await fetch(`${BASE}/upload`, {
      method: "POST",
      body: formData,
      credentials: "include",
      headers: { "X-CSRF-Token": getCsrfToken() },
    });
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail || `Upload failed: ${res.status}`);
    }
    const raw = await res.json();
    return toUploadResult(raw, file);
  },

  /** Upload a ZIP folder for multi-file profiling. */
  uploadFolder: async (
    file: File,
    projectId?: string,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<FolderUploadResult> => {
    const formData = new FormData();
    formData.append("file", file);
    if (projectId) formData.append("project_id", projectId);

    const raw = await uploadMultipartWithProgress(
      `${BASE}/upload-folder`,
      formData,
      onProgress,
    );
    return {
      session_id: raw.session_id || "",
      source_name: raw.source_name || file.name,
      source_type: "folder",
      status: "processing",
    };
  },

  /**
   * Upload multiple loose files (a folder-picker or multi-select
   * selection) for multi-file profiling — sent as-is over multipart/
   * form-data, no client-side ZIP step. The server builds the archive
   * (streamed straight to disk) instead of the browser building one in
   * memory, which used to crash with "Array buffer allocation failed"
   * once the selection added up to a few hundred MB.
   */
  uploadFiles: async (
    files: File[],
    projectId?: string,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<FolderUploadResult> => {
    const formData = new FormData();
    for (const f of files) {
      // webkitRelativePath preserves subfolder structure when the
      // selection came from a folder picker; the backend strips it back
      // down to the bare filename (flat storage, same as a single-file
      // upload-folder ZIP).
      const relPath =
        (f as File & { webkitRelativePath?: string }).webkitRelativePath ||
        f.name;
      formData.append("files", f, relPath);
    }
    if (projectId) formData.append("project_id", projectId);

    const raw = await uploadMultipartWithProgress(
      `${BASE}/upload-files`,
      formData,
      onProgress,
    );
    return {
      session_id: raw.session_id || "",
      source_name: raw.source_name || `${files.length}-file selection`,
      source_type: "folder",
      status: "processing",
    };
  },

  /** Poll the background pre-flight scan kicked off by uploadFolder/uploadFiles. */
  getFolderPreflightStatus: async (
    sessionId: string,
  ): Promise<FolderPreflightStatus> => {
    return request<FolderPreflightStatus>(
      `/folder-status/${encodeURIComponent(sessionId)}`,
    );
  },

  /** Apply include/exclude file selection after pre-flight review. */
  folderFilter: async (
    params: FolderFilterRequest,
  ): Promise<FolderFilterResult> => {
    // Backend wants relative-path lists, not file_id lists. Resolve via state.
    // For now, we send both lists in both places — the backend currently accepts
    // a list of paths via `include`/`exclude`. If the caller has file IDs we
    // cannot resolve here, we pass them through unchanged so the backend has
    // the best chance of matching.
    const body: Record<string, unknown> = { session_id: params.session_id };
    if (params.include_file_ids) body.include = params.include_file_ids;
    if (params.exclude_file_ids) body.exclude = params.exclude_file_ids;

    const raw = await request<Record<string, any>>("/folder-filter", {
      method: "POST",
      body: safeStringify(body),
    });
    return {
      processable_count: raw.processable_count || 0,
      selected_files: Array.isArray(raw.selected_files)
        ? raw.selected_files
        : Array.isArray(raw.files)
          ? raw.files
          : [],
    };
  },

  /** Connect to a database for profiling. */
  dbConnect: async (
    config: Record<string, unknown>,
  ): Promise<DBConnectResult> => {
    const raw = await request<Record<string, any>>("/db-connect", {
      method: "POST",
      body: safeStringify(config),
    });
    return toDBConnectResult(raw);
  },

  /**
   * List schemas visible on an already-connected session's live database
   * connection, without re-entering connection details (#682).
   */
  listSchemas: async (sessionId: string): Promise<SchemaListResult> => {
    const raw = await request<Record<string, any>>(
      `/schemas/${encodeURIComponent(sessionId)}`,
    );
    return {
      schemas: Array.isArray(raw.schemas) ? raw.schemas : [],
      current_schema: raw.current_schema ?? null,
    };
  },

  /**
   * Switch an already-connected session to a different schema on the
   * same live connection, refreshing the discovered-tables list (#682).
   * Same response shape as `dbConnect`.
   */
  selectSchema: async (
    sessionId: string,
    schemaName: string,
  ): Promise<DBConnectResult> => {
    const raw = await request<Record<string, any>>("/schema-select", {
      method: "POST",
      body: safeStringify({ session_id: sessionId, schema_name: schemaName }),
    });
    return toDBConnectResult(raw);
  },

  /** Select which tables to profile (required for DB sources). */
  selectTables: async (
    sessionId: string,
    tables: string[],
  ): Promise<{ session_id: string; selected_tables: string[] }> => {
    return request("/select-tables", {
      method: "POST",
      body: safeStringify({ session_id: sessionId, tables }),
    });
  },

  /** Start a profiling run. */
  startRun: async (params: StartProfileRequest): Promise<RunStatus> => {
    // Backend expects `session_id` as the primary identifier, not `source_id`.
    // `project_id` is wired through so the completed run gets persisted with
    // a project link — without it `RunHistoryView` filtered by project_id
    // sees an empty list (FR105-FR109).
    const body: Record<string, unknown> = {
      session_id: params.source_id,
      sample_rows: params.sample_rows,
      run_in_background: true,
      include_dq: params.assess_quality !== false,
      include_keys: params.detect_keys !== false,
      include_relationships: params.detect_relationships !== false,
      deep_relationships: params.deep_relationships === true,
      include_ge_suite: true,
    };
    if (params.project_id) body.project_id = params.project_id;
    const raw = await request<Record<string, any>>("/run", {
      method: "POST",
      body: safeStringify(body),
    });
    return toRunStatus(raw);
  },

  /** Read-only poll target for the secondary malware-scan verdict — used
   *  after `startRun` 409s with a `secondary_scan_pending` code, so the
   *  caller can wait for the async cloud-native scan without repeatedly
   *  re-invoking `startRun` itself just to check status. */
  getScanStatus: async (
    sessionId: string,
  ): Promise<SecondaryScanStatusResult> => {
    return request(`/scan-status/${encodeURIComponent(sessionId)}`);
  },

  /** Poll run status. */
  getStatus: async (sessionId: string): Promise<RunStatus> => {
    const raw = await request<Record<string, any>>(`/status/${sessionId}`);
    return toRunStatus(raw);
  },

  /** Fetch full profiling results once complete. */
  getResults: async (sessionId: string): Promise<ProfileResults> => {
    const raw = await request<Record<string, any>>(`/results/${sessionId}`);
    return toProfileResults(raw);
  },

  /** Fetch DQ assessment. */
  getDQ: async (sessionId: string): Promise<DQAssessment> => {
    const raw = await request<Record<string, any>>(`/dq/${sessionId}`);
    const dq_issues = Array.isArray(raw.issues)
      ? raw.issues
      : Array.isArray(raw.dq_issues)
        ? raw.dq_issues
        : [];
    // Derive dimension_scores from severity-weighted issue counts when the
    // backend doesn't provide them. Each issue penalises its category
    // (completeness / uniqueness / consistency / validity) proportional to
    // severity (medium=5, high=10, critical=20). Score = 100 − penalty,
    // clamped at 0.
    const dims = raw.dimension_scores || {};
    if (!raw.dimension_scores) {
      const penalty: Record<string, number> = {
        completeness: 0,
        uniqueness: 0,
        consistency: 0,
        validity: 0,
      };
      const severityWeight: Record<string, number> = {
        low: 2,
        medium: 5,
        high: 10,
        critical: 20,
      };
      for (const i of dq_issues) {
        const cat = (i?.category || "validity").toLowerCase();
        const w = severityWeight[(i?.severity || "medium").toLowerCase()] ?? 5;
        if (cat in penalty) penalty[cat] += w;
      }
      for (const k of Object.keys(penalty)) {
        dims[k] = Math.max(0, 100 - penalty[k]);
      }
    }
    return {
      session_id: raw.session_id || sessionId,
      dimension_scores: dims,
      // Normalise `table` from the backend's `table_name` so the Findings tab's
      // "Tables with issues" count + per-table filter work (they key on
      // `issue.table`, which the /dq payload delivers as `table_name`).
      issues: dq_issues.map((i: DQIssue) => ({
        ...i,
        table: i.table ?? i.table_name,
      })),
      rules: Array.isArray(raw.rules)
        ? raw.rules
        : Array.isArray(raw.dq_rules)
          ? raw.dq_rules
          : [],
      remediation_plan: raw.remediation_plan,
      ge_suite_by_table: raw.ge_suite || raw.ge_suite_by_table || undefined,
    };
  },

  /** Fetch detected relationships. */
  getRelationships: async (sessionId: string): Promise<RelationshipResults> => {
    const raw = await request<Record<string, any>>(
      `/relationships/${sessionId}`,
    );
    return {
      relationships: Array.isArray(raw.relationships) ? raw.relationships : [],
      graph: raw.graph || raw.relationship_graph,
      pk_candidates: raw.pk_candidates || {},
      fk_candidates: raw.fk_candidates || {},
    };
  },

  /**
   * SSE progress stream URL. Backend emits `table.profile_start`,
   * `table.profile_complete`, `table.profile_error`, and `session.complete`
   * events on this endpoint. The useSSEStream hook handles reconnection
   * and Last-Event-ID resume automatically.
   */
  getProgressURL: (sessionId: string): string =>
    `${BASE}/progress/${sessionId}`,

  /** Trigger LLM interpretation of results. */
  interpret: async (sessionId: string): Promise<Record<string, unknown>> => {
    return request<Record<string, unknown>>("/interpret", {
      method: "POST",
      body: safeStringify({ session_id: sessionId }),
    });
  },

  /**
   * Generate LLM-synthesized business-insight findings.
   *
   * Admin-gated by the `feature_flags.profiler_llm_insights` config
   * entry; returns 403 when disabled and 503 when enabled but no LLM
   * provider is configured for the project. The deterministic
   * `business_insight` detectors run on every profile regardless of
   * this endpoint — this layers LLM-narrated findings on top.
   *
   * On success the backend has already appended the new findings to
   * the session's `dq_issues`; callers should re-fetch via `getDQ`
   * (or `fetchResults`) to refresh local state.
   */
  generateLlmInsights: async (
    sessionId: string,
  ): Promise<Record<string, unknown>> => {
    return request<Record<string, unknown>>(
      `/runs/${encodeURIComponent(sessionId)}/llm-insights`,
      { method: "POST" },
    );
  },

  /**
   * Read a feature flag from the v2 config API.
   * Returns ``false`` when the entry is absent or any error occurs —
   * fail-closed: a flag must be explicitly set to ``"true"`` to be on.
   */
  getFeatureFlag: async (key: string): Promise<boolean> => {
    try {
      const raw = await fetch(
        `/api/v2/config/feature_flags/${encodeURIComponent(key)}`,
        { credentials: "include" },
      );
      if (!raw.ok) return false;
      const body = await raw.json();
      // ConfigEntry shape: { value: string, ... }. Accept both
      // top-level shape and APIEnvelope-wrapped { data: {...} }.
      const value = body?.data?.value ?? body?.value;
      return typeof value === "string" && value.toLowerCase() === "true";
    } catch {
      return false;
    }
  },

  /**
   * Profiler config — currently stored in-memory in the frontend only.
   * Backend has no /config endpoint; we return local defaults. When the
   * backend adds persistent config, this adapter can switch to calling it.
   */
  getConfig: async (): Promise<ProfilerConfig> => ({
    max_sample_rows: 10000,
    pii_detection_model: "regex",
    pii_confidence_threshold: 0.7,
    column_timeout_seconds: 30,
  }),

  updateConfig: async (
    update: ProfilerConfigUpdate,
  ): Promise<ProfilerConfig> => {
    const current: ProfilerConfig = {
      max_sample_rows: 10000,
      pii_detection_model: "regex",
      pii_confidence_threshold: 0.7,
      column_timeout_seconds: 30,
    };
    return { ...current, ...update };
  },

  // ── Run history (FR105-FR109) ──────────────────────────────────

  /**
   * List historical profiling runs for a project (most recent first).
   * Returns lightweight summaries — call ``getRun`` to load the full
   * snapshot for one run.
   */
  listRuns: async (params: {
    project_id?: string;
    session_id?: string;
    limit?: number;
    offset?: number;
    include_deleted?: boolean;
  }): Promise<RunHistoryListResponse> => {
    const qs = new URLSearchParams();
    if (params.project_id) qs.set("project_id", params.project_id);
    if (params.session_id) qs.set("session_id", params.session_id);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    if (params.include_deleted) qs.set("include_deleted", "true");
    return request<RunHistoryListResponse>(`/runs?${qs.toString()}`);
  },

  /** Open a historical run — full snapshot (output + metadata). */
  getRun: async (
    runId: string,
    includeDeleted = false,
  ): Promise<RunHistoryDetail> => {
    const qs = includeDeleted ? "?include_deleted=true" : "";
    return request<RunHistoryDetail>(`/runs/${encodeURIComponent(runId)}${qs}`);
  },

  /** Side-by-side comparison of two runs (FR108). */
  compareRuns: async (
    baseRunId: string,
    compareRunId: string,
  ): Promise<RunComparisonResponse> => {
    const qs = new URLSearchParams({
      base: baseRunId,
      compare: compareRunId,
    });
    return request<RunComparisonResponse>(`/runs/compare?${qs.toString()}`);
  },

  /** Soft-delete a run (metadata retained per FR109 audit). */
  deleteRun: async (
    runId: string,
  ): Promise<{ run_id: string; deleted: boolean }> => {
    return request(`/runs/${encodeURIComponent(runId)}`, { method: "DELETE" });
  },
};

// ── Run-history response types ────────────────────────────────────

/**
 * Lightweight run summary used by the history list and compare picker.
 * Matches ``_run_record_to_summary`` in data_profiling_routes.py.
 */
export interface RunHistorySummary {
  run_id: string;
  session_id: string;
  project_id: string;
  started_at: string;
  completed_at: string;
  status: "pending" | "running" | "completed" | "failed";
  tables_profiled: number;
  tables_total: number;
  aggregate_score: number;
  elapsed_seconds: number;
  source_name: string;
  source_type: string;
  deleted?: boolean;
  /** Which phases the run had on; absent (older backend) or all true means a full run. */
  run_options?: RunOptions;
}

export interface RunHistoryListResponse {
  total: number;
  limit: number;
  offset: number;
  runs: RunHistorySummary[];
}

/** Full historical snapshot — list summary + the full output payload. */
export interface RunHistoryDetail extends RunHistorySummary {
  output: Record<string, unknown>;
  metadata: Record<string, unknown>;
}

export interface RunComparisonResponse {
  base_run: RunHistorySummary;
  compare_run: RunHistorySummary;
  summary: {
    base_run_id: string;
    compare_run_id: string;
    aggregate_score_delta: number;
    tables_added: number;
    tables_removed: number;
    schema_changes: number;
    new_issues: number;
    resolved_issues: number;
    has_changes: boolean;
    /** False when the two runs had different phases on, so scores and issue counts are not like-for-like. */
    like_for_like?: boolean;
    comparison_caveat?: string | null;
  };
  table_score_deltas: Array<{
    table: string;
    old_score: number;
    new_score: number;
    delta: number;
  }>;
  schema_changes: Array<{
    table: string;
    column: string;
    change_type: "added" | "removed" | "type_changed" | "nullable_changed";
    old_value?: string | null;
    new_value?: string | null;
  }>;
  issue_changes: Array<{
    table: string;
    issue: string;
    change_type: "new" | "resolved" | "persistent";
    severity: string;
  }>;
  tables_added: string[];
  tables_removed: string[];
}

/**
 * Export a finished profiling session as a PDF or PowerPoint report.
 * Renders inline, in-process, in a single request -- same synchronous
 * pattern as {@link exportFindingsExcel} -- instead of the Celery
 * enqueue/poll/download job used by POST /api/v2/reports. The render tools
 * themselves (reportlab / python-pptx) are pure-Python and fast; the async
 * job path added no benefit here and was the source of the reported hang.
 */
export async function exportProfileReport(
  sessionId: string,
  format: "pdf" | "pptx",
): Promise<void> {
  const res = await fetch(
    `/api/profiling-agent/export-report/${sessionId}/${format}`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": getCsrfToken() },
      credentials: "include",
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Report export failed (${res.status}).`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ztd-profile-${sessionId}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Fast, LLM-free findings → Excel workbook download (for ITSM import). */
export async function exportFindingsExcel(
  sessionId: string,
  ruleIds?: string[] | null,
): Promise<void> {
  const res = await fetch(
    `/api/profiling-agent/export-findings-excel/${sessionId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      credentials: "include",
      body: JSON.stringify({ rule_ids: ruleIds ?? null }),
    },
  );
  if (!res.ok) throw new Error(`Excel export failed (${res.status}).`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ztd-findings-${sessionId}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** One finding's violation-query availability (tier 1). No SQL: the query itself is a download. */
export interface ViolationListing {
  violation_id: string;
  finding_id: string | null;
  has_query: boolean;
  unavailable: string | null;
  count_note: string | null;
}

/** Which findings of a session have a violation query (and why the others do not). */
export async function listViolationQueries(
  sessionId: string,
): Promise<{ db_type: string; findings: ViolationListing[] }> {
  const res = await fetch(`/api/profiling-agent/violations/${sessionId}`, {
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      body.detail || `Violation queries unavailable (${res.status}).`,
    );
  }
  return res.json();
}

function saveAnchor(href: string, filename?: string): void {
  const a = document.createElement("a");
  a.href = href;
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Download one finding's violation query (tier 1): the read-only SELECT that returns its failing rows, in the
 * source's own SQL dialect. Logic only, no client data; a plain GET so the browser saves it directly.
 */
export function downloadViolationQuery(
  sessionId: string,
  violationId: string,
): void {
  // A download name, as the MDM export does: an error response (404 after the session expired, 422) is then a failed
  // download instead of navigating the tab away from the profiler to raw JSON.
  saveAnchor(
    `/api/profiling-agent/violations/${sessionId}/${violationId}/query`,
    `ztd-violation-${violationId}.sql`,
  );
}

/** Download every finding's violation query (or the selected ones) as one zip with a manifest (tier 1). */
export async function downloadViolationBundle(
  sessionId: string,
  violationIds?: string[] | null,
): Promise<void> {
  const res = await fetch(
    `/api/profiling-agent/violations/${sessionId}/bundle`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      credentials: "include",
      body: JSON.stringify({ violation_ids: violationIds ?? null }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      body.detail || `Violation query export failed (${res.status}).`,
    );
  }
  const url = URL.createObjectURL(await res.blob());
  saveAnchor(url, `ztd-violations-${sessionId}.zip`);
  URL.revokeObjectURL(url);
}

/**
 * Download one finding's failing rows as a CSV streamed from the source (tier 2: capped, audited, personal data masked
 * unless the caller holds the unmasked permission). A plain GET so the browser streams it to disk; the file's last
 * line says how it ended (`# end`, `# truncated`, `# aborted`).
 */
export function downloadViolationExtract(
  sessionId: string,
  violationId: string,
): void {
  saveAnchor(
    `/api/profiling-agent/violations/${sessionId}/${violationId}/extract.csv`,
    `ztd-violations-${sessionId.slice(0, 8)}-${violationId}.csv`,
  );
}

/**
 * Queue a Scrum-Planner backlog session from selected findings (v2). The LLM
 * analysis runs asynchronously in the worker (a synchronous in-request call
 * here could 504 on a slow model response over a large selection) — this
 * returns a job to poll via `api.backlogGetJob`, not the session directly.
 */
export async function sendFindingsToBacklog(
  projectId: string,
  sessionId: string,
  ruleIds?: string[] | null,
): Promise<{
  job_id: string;
  status: string;
  kind: string;
  findings_count: number;
}> {
  const res = await fetch(
    `/api/v2/projects/${projectId}/backlog/from-findings`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      credentials: "include",
      body: JSON.stringify({
        session_id: sessionId,
        rule_ids: ruleIds ?? null,
      }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Backlog export failed (${res.status}).`);
  }
  return res.json();
}
